import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import RichTextEditor from '../components/RichTextEditor';
import {
  Mail, Phone, Search, Users, Send, UserPlus, Filter,
  ChevronLeft, ChevronRight, Loader2, CheckCircle, XCircle,
  Calendar, TrendingUp, MessageSquare, BarChart3, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMarketing = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  // Stats
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  
  // Users
  const [emailUsers, setEmailUsers] = useState([]);
  const [phoneUsers, setPhoneUsers] = useState([]);
  const [totalEmailUsers, setTotalEmailUsers] = useState(0);
  const [totalPhoneUsers, setTotalPhoneUsers] = useState(0);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filters
  const [filters, setFilters] = useState({
    subscription_status: 'all',
    has_card: null,
    search: '',
    signup_date_from: '',
    signup_date_to: ''
  });
  
  // Selection
  const [selectedEmailUsers, setSelectedEmailUsers] = useState([]);
  const [selectedPhoneUsers, setSelectedPhoneUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('email'); // 'email' or 'phone'
  
  // Email compose
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [usePlainTextEditor, setUsePlainTextEditor] = useState(false); // Toggle for plain text mode
  
  // SMS compose
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsMessage, setSmsMessage] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  
  // Create contacts
  const [showCreateContactsModal, setShowCreateContactsModal] = useState(false);
  const [creatingContacts, setCreatingContacts] = useState(false);
  const [contactTags, setContactTags] = useState('marketing');
  
  // Campaigns
  const [campaigns, setCampaigns] = useState([]);
  const [showCampaignsModal, setShowCampaignsModal] = useState(false);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!user) return;
    try {
      setLoadingStats(true);
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/marketing/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoadingStats(false);
    }
  }, [user]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    if (!user) return;
    try {
      setLoadingUsers(true);
      const token = await user.getIdToken();
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50'
      });
      
      if (filters.subscription_status && filters.subscription_status !== 'all') {
        params.append('subscription_status', filters.subscription_status);
      }
      if (filters.has_card !== null) {
        params.append('has_card', filters.has_card.toString());
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
      if (filters.signup_date_from) {
        params.append('signup_date_from', filters.signup_date_from);
      }
      if (filters.signup_date_to) {
        params.append('signup_date_to', filters.signup_date_to);
      }
      
      const response = await fetch(`${API_URL}/api/marketing/users?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmailUsers(data.email_users || []);
        setPhoneUsers(data.phone_users || []);
        setTotalEmailUsers(data.total_email_users || data.email_users?.length || 0);
        setTotalPhoneUsers(data.total_phone_users || data.phone_users?.length || 0);
        setTotalPages(data.total_pages || 1);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: 'Error',
        description: 'Failed to load users',
        variant: 'destructive'
      });
    } finally {
      setLoadingUsers(false);
    }
  }, [user, page, filters, toast]);

  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/marketing/campaigns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data.campaigns || []);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  }, [user]);

  useEffect(() => {
    fetchStats();
    fetchUsers();
  }, [fetchStats, fetchUsers]);

  // Handle select all
  const handleSelectAll = (type) => {
    if (type === 'email') {
      if (selectedEmailUsers.length === emailUsers.length) {
        setSelectedEmailUsers([]);
      } else {
        setSelectedEmailUsers(emailUsers.map(u => u.id || u.userId));
      }
    } else {
      if (selectedPhoneUsers.length === phoneUsers.length) {
        setSelectedPhoneUsers([]);
      } else {
        setSelectedPhoneUsers(phoneUsers.map(u => u.id || u.userId));
      }
    }
  };

  // Handle individual selection
  const handleSelectUser = (type, userId) => {
    if (type === 'email') {
      setSelectedEmailUsers(prev => 
        prev.includes(userId) 
          ? prev.filter(id => id !== userId)
          : [...prev, userId]
      );
    } else {
      setSelectedPhoneUsers(prev => 
        prev.includes(userId) 
          ? prev.filter(id => id !== userId)
          : [...prev, userId]
      );
    }
  };

  // Send bulk email
  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailContent.trim()) {
      toast({
        title: 'Missing Content',
        description: 'Please enter subject and email content',
        variant: 'destructive'
      });
      return;
    }
    
    if (selectedEmailUsers.length === 0) {
      toast({
        title: 'No Recipients',
        description: 'Please select at least one user to send email',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      setSendingEmail(true);
      const token = await user.getIdToken();
      
      const response = await fetch(`${API_URL}/api/marketing/send-bulk-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_ids: selectedEmailUsers,
          subject: emailSubject,
          html_content: emailContent,
          plain_content: emailContent.replace(/<[^>]*>/g, '')
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: 'Emails Sent!',
          description: data.message
        });
        setShowComposeModal(false);
        setEmailSubject('');
        setEmailContent('');
        setSelectedEmailUsers([]);
        fetchStats();
      } else {
        throw new Error(data.detail || 'Failed to send emails');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setSendingEmail(false);
    }
  };

  // Send SMS to selected phone users
  const handleSendSms = async () => {
    if (!smsMessage.trim()) {
      toast({
        title: 'Missing Message',
        description: 'Please enter SMS message content',
        variant: 'destructive'
      });
      return;
    }
    
    if (selectedPhoneUsers.length === 0) {
      toast({
        title: 'No Recipients',
        description: 'Please select at least one user to send SMS',
        variant: 'destructive'
      });
      return;
    }
    
    // Check message length (SMS limit is typically 160 chars)
    if (smsMessage.length > 160) {
      toast({
        title: 'Message Too Long',
        description: `SMS message is ${smsMessage.length} characters. Keep it under 160 for a single SMS.`,
        variant: 'destructive'
      });
      return;
    }
    
    try {
      setSendingSms(true);
      const token = await user.getIdToken();
      
      console.log('[SMS] Sending to user_ids:', selectedPhoneUsers);
      
      const response = await fetch(`${API_URL}/api/marketing/send-bulk-sms`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_ids: selectedPhoneUsers,
          message: smsMessage
        })
      });
      
      const data = await response.json();
      console.log('[SMS] Response:', data);
      
      if (response.ok) {
        toast({
          title: 'SMS Sent!',
          description: data.message
        });
        setShowSmsModal(false);
        setSmsMessage('');
        setSelectedPhoneUsers([]);
        fetchStats();
      } else {
        throw new Error(data.detail || 'Failed to send SMS');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setSendingSms(false);
    }
  };

  // Create contacts from users
  const handleCreateContacts = async () => {
    const selectedUsers = activeTab === 'email' ? selectedEmailUsers : selectedPhoneUsers;
    
    if (selectedUsers.length === 0) {
      toast({
        title: 'No Users Selected',
        description: 'Please select users to create contacts',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      setCreatingContacts(true);
      const token = await user.getIdToken();
      
      const response = await fetch(`${API_URL}/api/marketing/create-contacts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_ids: selectedUsers,
          tags: contactTags.split(',').map(t => t.trim()).filter(Boolean)
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: 'Contacts Created!',
          description: data.message
        });
        setShowCreateContactsModal(false);
        if (activeTab === 'email') {
          setSelectedEmailUsers([]);
        } else {
          setSelectedPhoneUsers([]);
        }
      } else {
        throw new Error(data.detail || 'Failed to create contacts');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setCreatingContacts(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const currentUsers = activeTab === 'email' ? emailUsers : phoneUsers;
  const selectedUsers = activeTab === 'email' ? selectedEmailUsers : selectedPhoneUsers;

  return (
    <div className="space-y-6" data-testid="admin-marketing">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
                <p className="text-xs text-gray-500">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Mail className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.users_with_email || 0}</p>
                <p className="text-xs text-gray-500">With Email</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Phone className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.users_with_phone || 0}</p>
                <p className="text-xs text-gray-500">With Phone</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Send className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.campaigns?.total_emails_sent || 0}</p>
                <p className="text-xs text-gray-500">Emails Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Subscription Breakdown */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-amber-600" />
            User Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
              <span className="text-sm">Free: {stats?.subscription_breakdown?.free || 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
              <span className="text-sm">Pro: {stats?.subscription_breakdown?.pro || 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-teal-500 rounded-full"></div>
              <span className="text-sm">Business: {stats?.subscription_breakdown?.business || 0}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-600" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search email, phone, name..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="pl-9"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Subscription</label>
              <Select
                value={filters.subscription_status}
                onValueChange={(value) => setFilters(prev => ({ ...prev, subscription_status: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="free">Free Users</SelectItem>
                  <SelectItem value="pro">Pro Users</SelectItem>
                  <SelectItem value="business">Business Users</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Has Card</label>
              <Select
                value={filters.has_card === null ? 'all' : filters.has_card.toString()}
                onValueChange={(value) => setFilters(prev => ({ 
                  ...prev, 
                  has_card: value === 'all' ? null : value === 'true' 
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="true">With Card</SelectItem>
                  <SelectItem value="false">Without Card</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-end gap-2">
              <Button
                onClick={() => { setPage(1); fetchUsers(); }}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Apply
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Lists */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex gap-2">
              <Button
                variant={activeTab === 'email' ? 'default' : 'outline'}
                onClick={() => setActiveTab('email')}
                className={activeTab === 'email' ? 'bg-amber-600 hover:bg-amber-700' : ''}
              >
                <Mail className="w-4 h-4 mr-2" />
                Email Users ({totalEmailUsers})
              </Button>
              <Button
                variant={activeTab === 'phone' ? 'default' : 'outline'}
                onClick={() => setActiveTab('phone')}
                className={activeTab === 'phone' ? 'bg-purple-600 hover:bg-purple-700' : ''}
              >
                <Phone className="w-4 h-4 mr-2" />
                Phone Users ({totalPhoneUsers})
              </Button>
            </div>
            
            <div className="flex gap-2">
              {activeTab === 'email' && selectedEmailUsers.length > 0 && (
                <Button
                  onClick={() => setShowComposeModal(true)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Email ({selectedEmailUsers.length})
                </Button>
              )}
              {activeTab === 'phone' && selectedPhoneUsers.length > 0 && (
                <Button
                  onClick={() => setShowSmsModal(true)}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Send SMS ({selectedPhoneUsers.length})
                </Button>
              )}
              {selectedUsers.length > 0 && (
                <Button
                  onClick={() => setShowCreateContactsModal(true)}
                  variant="outline"
                  className="border-amber-600 text-amber-600 hover:bg-amber-50"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create Contacts ({selectedUsers.length})
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {loadingUsers ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
            </div>
          ) : currentUsers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No users found matching the filters
            </div>
          ) : (
            <>
              {/* Select All */}
              <div className="flex items-center gap-2 mb-4 pb-3 border-b">
                <Checkbox
                  checked={selectedUsers.length === currentUsers.length && currentUsers.length > 0}
                  onCheckedChange={() => handleSelectAll(activeTab)}
                />
                <span className="text-sm font-medium">
                  Select All ({currentUsers.length} users)
                </span>
              </div>
              
              {/* User List */}
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {currentUsers.map((u) => (
                  <div
                    key={u.id || u.userId}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <Checkbox
                      checked={selectedUsers.includes(u.id || u.userId)}
                      onCheckedChange={() => handleSelectUser(activeTab, u.id || u.userId)}
                    />
                    
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={u.photoURL} />
                      <AvatarFallback className="bg-amber-100 text-amber-700">
                        {(u.displayName || u.email || 'U')[0].toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {u.displayName || u.cardName || 'Unknown'}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        {activeTab === 'email' ? (
                          <span className="truncate">{u.email}</span>
                        ) : (
                          <span>{u.phone}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {u.hasCard && (
                        <Badge variant="outline" className="text-xs">
                          Has Card
                        </Badge>
                      )}
                      <Badge
                        className={`text-xs ${
                          u.subscriptionTier === 'pro' 
                            ? 'bg-amber-100 text-amber-700' 
                            : u.subscriptionTier === 'business'
                            ? 'bg-teal-100 text-teal-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {u.subscriptionTier || 'Free'}
                      </Badge>
                    </div>
                    
                    <div className="text-xs text-gray-400">
                      {formatDate(u.createdAt)}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <span className="text-sm text-gray-500">
                  Page {page} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Email Compose Modal */}
      <Dialog open={showComposeModal} onOpenChange={setShowComposeModal}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-green-600" />
              Compose Email
            </DialogTitle>
            <DialogDescription>
              Send email to {selectedEmailUsers.length} selected users
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Subject *</label>
              <Input
                placeholder="Enter email subject..."
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700">Content *</label>
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={usePlainTextEditor}
                    onChange={(e) => {
                      setUsePlainTextEditor(e.target.checked);
                      setEmailContent(''); // Clear content when switching modes
                    }}
                    className="rounded border-gray-300"
                  />
                  Plain Text Mode
                </label>
              </div>
              {usePlainTextEditor ? (
                <textarea
                  value={emailContent}
                  onChange={(e) => setEmailContent(e.target.value)}
                  placeholder="Paste or type your email content here...&#10;&#10;Use * or - for bullet points:&#10;* First item&#10;* Second item"
                  className="w-full min-h-[300px] p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent font-mono text-sm"
                />
              ) : (
                <RichTextEditor
                  value={emailContent}
                  onChange={setEmailContent}
                  placeholder="Compose your email content..."
                  className="min-h-[300px]"
                />
              )}
              {usePlainTextEditor && (
                <p className="text-xs text-gray-500 mt-1">
                  Tip: Use * or - at the start of a line for bullet points. Line breaks will be preserved.
                </p>
              )}
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setShowComposeModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendEmail}
                disabled={sendingEmail || !emailSubject.trim() || !emailContent.trim()}
                className="bg-green-600 hover:bg-green-700"
              >
                {sendingEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send to {selectedEmailUsers.length} Users
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* SMS Compose Modal */}
      <Dialog open={showSmsModal} onOpenChange={setShowSmsModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-600" />
              Compose SMS
            </DialogTitle>
            <DialogDescription>
              Send SMS to {selectedPhoneUsers.length} selected users
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Message * <span className="text-gray-400 font-normal">({smsMessage.length}/160 characters)</span>
              </label>
              <textarea
                placeholder="Enter your SMS message..."
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                rows={4}
                maxLength={160}
              />
              {smsMessage.length > 140 && (
                <p className="text-xs text-amber-600 mt-1">
                  {160 - smsMessage.length} characters remaining
                </p>
              )}
            </div>
            
            <div className="bg-purple-50 p-3 rounded-lg">
              <p className="text-sm text-purple-700">
                <strong>Note:</strong> SMS will be sent via Twilio. Standard SMS rates apply.
                Messages over 160 characters will be split into multiple SMS.
              </p>
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setShowSmsModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendSms}
                disabled={sendingSms || !smsMessage.trim()}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {sendingSms ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send to {selectedPhoneUsers.length} Users
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Contacts Modal */}
      <Dialog open={showCreateContactsModal} onOpenChange={setShowCreateContactsModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-amber-600" />
              Create Contacts
            </DialogTitle>
            <DialogDescription>
              Create CRM contacts from {selectedUsers.length} selected users for follow-up
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Tags (comma separated)</label>
              <Input
                placeholder="marketing, campaign, followup"
                value={contactTags}
                onChange={(e) => setContactTags(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Tags help organize contacts in your CRM pipeline
              </p>
            </div>
            
            <div className="bg-amber-50 p-4 rounded-lg">
              <h4 className="font-medium text-amber-800 mb-2">What happens next?</h4>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• Contacts will be created in your CRM pipeline</li>
                <li>• Duplicates (same email/phone) will be skipped</li>
                <li>• You can assign team members for follow-up</li>
              </ul>
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setShowCreateContactsModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateContacts}
                disabled={creatingContacts}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {creatingContacts ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Create {selectedUsers.length} Contacts
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMarketing;
